import React, { useContext, useEffect, useState } from 'react';
import {
  Typography,
  Card,
  Button,
  TextField,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { LoadingContext } from '../../context/LoadingContext';
import { getWalletBalance, rechargeWallet } from '../../services/api';

const StudentWallet = () => {
  const { setLoading } = useContext(LoadingContext);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [walletBalance, setWalletBalance] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false); // Confirmation dialog state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' }); // Snackbar state

  // Fetch wallet balance from API
  useEffect(() => {
    const fetchWalletBalance = async () => {
      setLoading(true);
      try {
        const response = await getWalletBalance();
        setWalletBalance(response.balance);
      } catch (error) {
        console.error('Error fetching wallet balance:', error);
        setSnackbar({ open: true, message: 'Failed to fetch wallet balance. Please try again later.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchWalletBalance();
  }, [setLoading]);

  // Handle recharge confirmation
  const handleRechargeConfirmation = () => {
    if (!rechargeAmount || isNaN(rechargeAmount) || rechargeAmount <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount to recharge.', severity: 'error' });
      return;
    }
    setOpenConfirmation(true); // Open confirmation dialog
  };

  // Handle recharge after confirmation
  const handleRecharge = async () => {
    setOpenConfirmation(false); // Close confirmation dialog
    try {
      setLoading(true);
      const response = await rechargeWallet(rechargeAmount);
      setWalletBalance(response.balance);
      setRechargeAmount('');
      setSnackbar({ open: true, message: `Recharge successful! New balance: $${response.balance}`, severity: 'success' });
    } catch (error) {
      console.error('Error recharging wallet:', error);
      setSnackbar({ open: true, message: 'Failed to recharge wallet. Please try again later.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Close Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Wallet Balance Section */}
      <Typography variant="h4" sx={{ fontFamily: 'Fredoka One', color: '#004aad', mb: 3 }}>
        Wallet
      </Typography>
      <Card
        sx={{
          padding: 3,
          backgroundColor: '#ffffff',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontFamily: 'Fredoka One', color: '#004aad', mb: 2 }}>
          Balance: {walletBalance !== null ? walletBalance : 'Loading...'} EGP
        </Typography>

        {/* Recharge Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Enter Amount"
            variant="outlined"
            type="number"
            value={rechargeAmount}
            onChange={(e) => setRechargeAmount(e.target.value)}
            sx={{ flex: 1 }}
            inputProps={{ min: 1 }} // Ensure positive numbers only
          />
          <Button
            variant="contained"
            onClick={handleRechargeConfirmation}
            disabled={!rechargeAmount || isNaN(rechargeAmount) || rechargeAmount <= 0} // Disable if invalid
            sx={{
              backgroundColor: '#004aad',
              '&:hover': { backgroundColor: '#003a8c' },
              fontFamily: 'Fredoka One',
              textTransform: 'none',
              fontSize: '1rem',
              padding: '10px 20px',
            }}
          >
            Recharge Wallet
          </Button>
        </Box>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        aria-labelledby="confirmation-dialog-title"
      >
        <DialogTitle id="confirmation-dialog-title">Confirm Recharge</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to recharge your wallet with {rechargeAmount} EGP?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmation(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleRecharge} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
};

export default StudentWallet;